import { useMemo, useState } from 'react';
import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { FolderKanban, Plus, Search } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Card, CardContent } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import { Textarea } from '@/components/atoms/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import {
  CustomerSelector,
  type CustomerSelectorContactResult,
} from '@/components/organisms/CustomerSelector';
import {
  ProjectStatusBadge,
  PROJECT_STATUS_LABELS,
} from '@/components/organisms/projects/ProjectStatusBadge';
import { useCreateProject, useProjects } from '@/hooks/projects/useProjects';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import type { Project } from '@/types/project';

type ProjectsSearch = {
  q?: string;
  status?: string;
  page?: number;
};

export const Route = createFileRoute('/_dashboard/dashboard/projects')({
  // Filters live in the URL (repo convention): deep links, refresh and the
  // back button all keep the view.
  validateSearch: (search: Record<string, unknown>): ProjectsSearch => {
    const next: ProjectsSearch = {};
    if (typeof search.q === 'string' && search.q) next.q = search.q;
    // Whitelist, don't pass through: an unknown status reaches the backend's
    // enum-validated query DTO and 400s the whole list on a stale deep link.
    if (
      typeof search.status === 'string' &&
      Object.prototype.hasOwnProperty.call(PROJECT_STATUS_LABELS, search.status)
    ) {
      next.status = search.status;
    }
    const page = Number(search.page);
    if (Number.isInteger(page) && page > 1) next.page = page;
    return next;
  },
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('jobs:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: ProjectsPage,
});

const PAGE_SIZE = 50;
const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'ALL', label: 'All statuses' },
  ...Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

function ProjectsPage() {
  const navigate = useNavigate();
  const { hasChildRoute } = useEntityRouteState({
    basePath: '/dashboard/projects',
    search: {},
  });
  const { q: search = '', status = 'ALL', page = 1 } = Route.useSearch();
  const [searchInput, setSearchInput] = useState(search);
  const [createOpen, setCreateOpen] = useState(false);

  const setFilters = (patch: Partial<ProjectsSearch> & { status?: string }) => {
    void navigate({
      to: '/dashboard/projects',
      search: (prev: ProjectsSearch) => {
        const next: ProjectsSearch = { ...prev, page: undefined, ...patch };
        if (!next.q) delete next.q;
        if (!next.status || next.status === 'ALL') delete next.status;
        if (!next.page || next.page <= 1) delete next.page;
        return next;
      },
      replace: true,
    });
  };

  const params = useMemo(
    () => ({
      q: search || undefined,
      status: status === 'ALL' ? undefined : status,
      page,
      limit: PAGE_SIZE,
    }),
    [search, status, page],
  );
  const { data, isLoading, isError } = useProjects(params);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageCount = data?.totalPages ?? 1;

  if (hasChildRoute) return <Outlet />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <FolderKanban className="h-6 w-6" />
            Projects
          </h1>
          <p className="text-muted-foreground text-sm">
            Group related jobs, estimates, invoices and costs under one umbrella — with
            schedule-of-values progress billing for commercial work.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          New project
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="text-muted-foreground absolute left-2 top-2.5 h-4 w-4" />
          <Input
            className="pl-8"
            placeholder="Search by name, number or description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setFilters({ q: searchInput });
            }}
          />
        </div>
        <Select value={status} onValueChange={(value) => setFilters({ status: value })}>
          <SelectTrigger className="w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Number</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[110px]">Start</TableHead>
                <TableHead className="w-[110px]">Target end</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                    Could not load projects.
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                    {search
                      ? `No projects match “${search}”.`
                      : 'No projects yet. Create one, or use “Create project from job” on a job.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((project: Project) => (
                  <TableRow
                    key={project.id}
                    className="cursor-pointer"
                    onClick={() =>
                      void navigate({
                        to: '/dashboard/projects/$projectId',
                        params: { projectId: project.id },
                        search: { tab: 'overview' },
                      })
                    }
                  >
                    <TableCell className="font-mono text-xs">{project.projectNumber}</TableCell>
                    <TableCell className="font-medium">{project.name}</TableCell>
                    <TableCell>
                      <ProjectStatusBadge status={project.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {project.startDate?.slice(0, 10) ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {project.targetEndDate?.slice(0, 10) ?? '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            {total} project{total === 1 ? '' : 's'} · page {page} of {pageCount}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setFilters({ page: Math.max(1, page - 1) })}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pageCount}
              onClick={() => setFilters({ page: page + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function CreateProjectDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetEndDate, setTargetEndDate] = useState('');
  const [contact, setContact] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const canSubmit = name.trim().length > 0 && Boolean(contact) && !createProject.isPending;

  function handleSelectContact(result: CustomerSelectorContactResult) {
    const id = result.id ?? result._id ?? result.contactId;
    if (!id) return;
    const label =
      result.displayName ||
      [result.firstName, result.lastName].filter(Boolean).join(' ') ||
      'Customer';
    setContact({ id, label });
  }

  async function handleSubmit() {
    if (!contact) return;
    const project = await createProject.mutateAsync({
      name: name.trim(),
      contactId: contact.id,
      description: description.trim() || undefined,
      startDate: startDate || undefined,
      targetEndDate: targetEndDate || undefined,
    });
    onOpenChange(false);
    setName('');
    setDescription('');
    setStartDate('');
    setTargetEndDate('');
    setContact(null);
    void navigate({
      to: '/dashboard/projects/$projectId',
      params: { projectId: project.id },
      search: { tab: 'overview' },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={name}
              placeholder="e.g. 4520 Oak St — full build-out"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Customer</Label>
            <CustomerSelector
              selected={contact ? { id: contact.id, name: contact.label } : null}
              onSelect={handleSelectContact}
              onClear={() => setContact(null)}
              placeholder="Search customers…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="project-description">Description</Label>
            <Textarea
              id="project-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="project-start">Start date</Label>
              <Input
                id="project-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="project-end">Target end</Label>
              <Input
                id="project-end"
                type="date"
                value={targetEndDate}
                onChange={(e) => setTargetEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => {
              // Rejection already surfaces via the mutation toast; keep the
              // dialog open with state intact so the user can retry.
              handleSubmit().catch(() => undefined);
            }}
          >
            Create project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
